import { preloadUrlConfig } from './preloadUrlConfig';
import { makeUrl } from './makeUrl';

import { TrelloWindow } from '@trello/window-types';
declare const window: TrelloWindow;

export type QuickloadError = [number, string];
export type QuickloadData = [JSON | null, XMLHttpRequest];

export type Callback = (
  err?: QuickloadError | null,
  data?: QuickloadData | null,
) => void;

interface Preload {
  isLoading: boolean;
  callbacks: Callback[];
  start: number;
  used: boolean;
  url: string;
  data?: QuickloadData | null;
  error?: QuickloadError | null;
}

import Queries from './quick-queries';

const parseJSON = function (data: string) {
  try {
    return JSON.parse(data);
  } catch (e) {
    return null;
  }
};

const ajaxGetJSON = function (
  url: string,
  next: Callback,
  operationName = 'unknown',
  operationSource = 'model-loader',
) {
  const xhr = new XMLHttpRequest();
  xhr.open('GET', url, true);
  xhr.setRequestHeader('Accept', 'application/json,text/plain');
  xhr.setRequestHeader(
    'X-Trello-Client-Version',
    window.trelloVersion || 'dev-0',
  );
  xhr.setRequestHeader('X-Trello-Operation-Source', operationSource);
  xhr.setRequestHeader('X-Trello-Operation-Name', operationName);
  xhr.onreadystatechange = function () {
    if (xhr.readyState === 4) {
      if (xhr.status !== 200) {
        next([xhr.status, xhr.responseText]);
      } else {
        next(null, [parseJSON(xhr.responseText), xhr]);
      }
    }
  };

  xhr.send(null);
};

// Lets see if there's any data we can pre-load
//
// The goal is to be able to anticipate which data URL we're going to need
// and load it ASAP.
//
// NOTE:
// We only want to avoid the *first* time that data URL gets loaded in the future,
// after that we'll load it fresh
const preloads: {
  [key: string]: Preload;
} = {};

const removePreload = function (url: string) {
  if (!(url in preloads)) {
    return;
  }
  delete preloads[url];
};

const preload = function (url: string, operationName = 'preload') {
  if (!url) {
    return;
  }
  const preloadObject: Preload = {
    isLoading: true,
    callbacks: [],
    start: Date.now(),
    used: false,
    url,
  };
  preloads[url] = preloadObject;

  ajaxGetJSON(
    url,
    function (err, data) {
      let callback;
      preloadObject.isLoading = false;
      if (err) {
        preloadObject.error = err;
        for (callback of Array.from(preloadObject.callbacks)) {
          callback(err);
        }
        return;
      }
      preloadObject.data = data;
      for (callback of Array.from(preloadObject.callbacks)) {
        callback(null, data);
      }
      // If the request isn't made within 10 seconds, it'll have to be made again
      setTimeout(() => removePreload(url), 10000);
    },
    operationName,
    'quickload',
  );
};

const cleanPreload = function (preload: Preload) {
  return {
    isLoading: preload.isLoading,
    start: preload.start,
    used: preload.used,
    url: preload.url,
  };
};

const isLoggedIn = () =>
  /token=/.test(document.cookie) || // prod
  /token3000=/.test(document.cookie) || // dev
  /token2999=/.test(document.cookie); // proxyserver

function getQuickloadUrls() {
  if (!isLoggedIn()) {
    return [];
  }

  const memberUrl = [
    makeUrl('/1/Members/me', Queries.memberHeader),
    'quick-queries:memberHeader',
  ];
  const path = window.location.pathname;
  const preloadMatch = preloadUrlConfig.find((preloadConfigEntry) =>
    preloadConfigEntry.pattern.test(path),
  );

  const preloadUrls =
    preloadMatch?.urls(preloadMatch.pattern.exec(path) as RegExpExecArray) ??
    [];

  return [memberUrl].concat(preloadUrls);
}

const quickload = {
  // The "init" method is called once from index.ejs to kick off preloading
  init() {
    const preloadUrls = getQuickloadUrls();
    for (const [url, operationName] of preloadUrls) {
      preload(url, operationName);
    }
  },
  getQuickloadUrls,
  makeUrl,
  load(url: string, next: Callback = () => {}, operationName: string) {
    const preloadObject = preloads[url];
    if (preloadObject !== undefined) {
      preloadObject.used = true;
      if (preloadObject.isLoading) {
        preloadObject.callbacks.push(next);
      } else {
        next(preloadObject.error, preloadObject.data);
        removePreload(url);
      }
      return cleanPreload(preloadObject);
    } else {
      ajaxGetJSON(url, next, operationName);
    }
  },
  hook(url: string) {
    return new Promise<QuickloadData | null | undefined>((resolve, reject) => {
      const preloadObject = preloads[url];
      if (preloadObject !== undefined) {
        preloadObject.used = true;
        if (preloadObject.isLoading) {
          preloadObject.callbacks.push((err, data) => {
            if (err) {
              reject(err);
            } else {
              resolve(data);
            }
          });
        } else {
          if (preloadObject.error) {
            reject(preloadObject.error);
          } else {
            resolve(preloadObject.data);
          }
        }
        return cleanPreload(preloadObject);
      } else {
        reject('Preload URL was never called');
      }
    });
  },
  clear() {
    for (const url in preloads) {
      removePreload(url);
    }
  },
};

export const QuickLoad = (window.QuickLoad = quickload);
